import { ProblemJSEnvironmentConfig } from "@gmm/problem";
import { useEffect, useState } from "react";

export interface EnvironmentConfig extends ProblemJSEnvironmentConfig{
  CLASS_LINKS_URL: string;
  ENVIRONMENT: string;
  HALL_MONITOR_URL: string;
  INTERCOM_APP_ID: string;
  LD_CLIENT_SIDE_ID: string;
  SENTRY_ENABLED: string;
  STUDENT_APP_URL: string;
  WEB_SENTRY_DSN: string;
}

let config: EnvironmentConfig | null = null;
let configPromise: Promise<EnvironmentConfig> | null = null;

// Initialize config by fetching config.json
async function initConfig(): Promise<EnvironmentConfig> {
  if (config) return config;
  if (configPromise) return configPromise;

  configPromise = (async () => {
    try {
      // query param to avoid caching
      const response = await fetch(`/config.json?t=${Date.now()}`);

      if (!response.ok) {
        throw new Error(`Config fetch failed: ${response.status}`);
      }

      const loadedConfig = await response.json();

      // Validate all required fields are present
      const requiredFields: Array<keyof EnvironmentConfig> = [
        'CLASS_LINKS_URL',
        'ENVIRONMENT',
        'HALL_MONITOR_URL',
        'INTERCOM_APP_ID',
        'LD_CLIENT_SIDE_ID',
        'SENTRY_ENABLED',
        'STUDENT_APP_URL',
        'WEB_SENTRY_DSN',
        'JAVA_URL',
        'FONT_URL',
      ];

      for (const field of requiredFields) {
        if (!(field in loadedConfig) || loadedConfig[field] === null) {
          throw new Error(`Missing or null required config field: ${field}`);
        }
      }

      config = loadedConfig as EnvironmentConfig;

      return config;
    } catch (error) {
      // Fallback defaults from build or for dev or fetch failure
      return getDefaultConfig();
    }
  })();

  return configPromise;
}

// Build a default config
export function getDefaultConfig(): EnvironmentConfig {
  return {
    CLASS_LINKS_URL:
      process.env.CLASS_LINKS_URL ||
      'host.docker.internal:3000/api/v2/class_codes',

    ENVIRONMENT: process.env.ENVIRONMENT || 'development',

    HALL_MONITOR_URL:
      process.env.HALL_MONITOR_URL || 'http://host.docker.internal:5000',

    INTERCOM_APP_ID: process.env.INTERCOM_APP_ID || '',

    LD_CLIENT_SIDE_ID: process.env.LD_CLIENT_SIDE_ID || '',

    SENTRY_ENABLED: process.env.SENTRY_ENABLED || 'false',

    STUDENT_APP_URL:
      process.env.STUDENT_APP_URL || 'http://host.docker.internal:8082',

    WEB_SENTRY_DSN: process.env.WEB_SENTRY_DSN || '',

    // Next two are passed to @gmm/problem
    JAVA_URL: process.env.MOBIUS_JAVA_URL || 'http://host.docker.internal:8080',
    ROBOTO_GMM: process.env.ROBOTO_GMM || 'fonts.vagrant.gmmdev.com',
  };
}

// Get config asynchronously
export async function getConfig(): Promise<EnvironmentConfig> {
  return initConfig();
}

// Hook to supply cached config to components if already loaded -- or load then supply it
export function useEnvConfig(): EnvironmentConfig | null {
  const [cfg, setCfg] = useState<EnvironmentConfig | null>(() => config);

  useEffect(() => {
    if (!cfg) {
      getConfig().then(setCfg);
    }
  }, [cfg]);

  return cfg;
}